import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../api/client';
import { TrashedDiagram } from '../api/types';

function daysSince(iso: string, locale: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
  if (days === 0) return locale.startsWith('fr') ? "aujourd'hui" : 'today';
  if (locale.startsWith('fr')) return `depuis ${days} jour${days > 1 ? 's' : ''}`;
  return `${days} day${days > 1 ? 's' : ''} ago`;
}

export function TrashModal({
  folderId,
  canPurge,
  onClose,
  onChanged,
}: {
  folderId: string;
  canPurge: boolean;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<TrashedDiagram[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    api
      .get<TrashedDiagram[]>(`/folders/${folderId}/trash`)
      .then(setItems)
      .catch((err) => setError(err instanceof ApiError ? err.message : String(err)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [folderId]);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleRestore = async (id: string) => {
    await api.post(`/diagrams/${id}/restore`);
    setSelected((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    load();
    onChanged();
  };

  const handlePurgeSelected = async () => {
    if (selected.size === 0) return;
    await api.post('/diagrams/purge', { ids: [...selected] });
    setSelected(new Set());
    load();
    onChanged();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('trash.title')}</h3>
          <button className="btn" onClick={onClose}>
            {t('common.close')}
          </button>
        </div>

        {error && <p className="error-text">{error}</p>}

        {loading ? (
          <p>{t('common.loading')}</p>
        ) : items.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>{t('trash.empty')}</p>
        ) : (
          <>
            <table>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id}>
                    {canPurge && (
                      <td style={{ width: 24 }}>
                        <input type="checkbox" checked={selected.has(item.id)} onChange={() => toggle(item.id)} />
                      </td>
                    )}
                    <td>
                      {item.name}
                      <br />
                      <span className="badge">{daysSince(item.deletedAt, i18n.language)}</span>
                    </td>
                    <td>
                      <button className="btn" onClick={() => handleRestore(item.id)}>
                        {t('trash.restore')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {canPurge && (
              <div className="toolbar" style={{ marginTop: 12 }}>
                <button className="btn btn-danger" disabled={selected.size === 0} onClick={handlePurgeSelected}>
                  {t('trash.purgeSelected', { count: selected.size })}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
